import type { ApolloClient } from '@apollo/client';
import { BehaviorSubject, map } from 'rxjs';
import { assign, fromEventObservable, fromPromise, setup, SnapshotFrom } from 'xstate';

import {
  ExtensionConversationQuery,
  ExtensionConversationQueryVariables,
  SvbrgConversationEntityVariant,
  TesseractDemandItemQuery,
  TesseractDemandItemQueryVariables,
} from '@/generated/gql/graphql';

import { EXTENSION_CONVERSATION_QUERY } from './loadConversationQuery.gql';
import { TESSERACT_DEMAND_ITEM_QUERY } from './loadDemandQuery.gql';

type Events =
  | {
      type: 'PURCHASE_REQ_NUMBER_UPDATED';
      purchaseReqNumber: string | null;
    }
  | { type: 'CLOSE_ALERT' }
  | { type: 'NAVIGATE_TO_DEMAND' };

export interface Context {
  apolloClient: ApolloClient<object>;
  purchaseReqNumber: string | null;
  purchaseReqNumberStream: BehaviorSubject<string | null>;
  demandId: string | null;
  previousPurchaseReqNumber: string | null;
}

export interface Input {
  apolloClient: ApolloClient<object>;
  purchaseReqNumberStream: BehaviorSubject<string | null>;
}

const getPurchaseReqNumberFromExternalFormat = (purchaseReqNumber: string) => {
  // Ivalua
  return purchaseReqNumber.replace(/^REQ0*/gi, '');
};

export const extensionPurchaseReqNumberMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Input,
  },

  actions: {
    setDemandConversationDetails: assign({
      demandId: (_, demandId: string | null) => demandId,
    }),
    setPurchaseReqNumber: assign(({ event, context }) => ({
      purchaseReqNumber:
        event.type === 'PURCHASE_REQ_NUMBER_UPDATED' ? event.purchaseReqNumber : context.purchaseReqNumber,
    })),
    resetContext: assign({
      purchaseReqNumber: null,
      demandId: null,
    }),
    resetDemandId: assign({
      demandId: null,
    }),
    setPreviousPurchaseReqNumber: assign({
      previousPurchaseReqNumber: ({ context }) => context.purchaseReqNumber,
    }),
    logError: ({ event }) => console.error(event),
  },

  actors: {
    subscribeToPurchaseReqNumberChanges: fromEventObservable(
      ({ input: purchaseReqNumberStream }: { input: BehaviorSubject<string | null> }) =>
        purchaseReqNumberStream.pipe<Events>(
          map((purchaseReqNumber) => ({
            type: 'PURCHASE_REQ_NUMBER_UPDATED',
            purchaseReqNumber,
          })),
        ),
    ),

    loadDemandConversation: fromPromise(
      async ({ input }: { input: { apolloClient: ApolloClient<object>; purchaseReqNumber: string } }) => {
        const { apolloClient, purchaseReqNumber } = input;

        const tesseractResult = await apolloClient.query<TesseractDemandItemQuery, TesseractDemandItemQueryVariables>({
          query: TESSERACT_DEMAND_ITEM_QUERY,
          variables: {
            filter: {
              combinator: 'and',
              rules: [
                {
                  field: 'referenceId',
                  operator: 'eq',
                  value: getPurchaseReqNumberFromExternalFormat(purchaseReqNumber),
                },
              ],
            },
          },
          fetchPolicy: 'no-cache',
        });

        const demandId = tesseractResult.data.tesseract.demands.nodes?.[0]?.id as string | undefined;
        const sequenceNumber = tesseractResult.data.tesseract.demands.nodes?.[0]?.sequenceNumber;

        if (!demandId || !sequenceNumber) {
          throw new Error('Demand details not found.');
        }

        const result = await apolloClient.query<ExtensionConversationQuery, ExtensionConversationQueryVariables>({
          query: EXTENSION_CONVERSATION_QUERY,
          variables: {
            entityId: demandId,
            entityVariant: SvbrgConversationEntityVariant.DEMAND,
          },
        });

        return result.data.sevenbridges?.conversationSettings?.id ? demandId : null;
      },
    ),
  },

  guards: {
    hasPurchaseReqNumber: ({ event }) =>
      event.type === 'PURCHASE_REQ_NUMBER_UPDATED' && Boolean(event.purchaseReqNumber),

    hasDemandConversationDetails: (_, demandId: string | null) => Boolean(demandId),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAcCuAnAxgCwIazACUwBHAOVQFsAjMdAQQBs6AXAYgAUBVQgYQAl6AZQCiAfUIiAimLJcAsgCERhMVw4ARegBURGgNoAGALqIUAe1gBLFlfMA7MyAAeiACwAmADQgAnu4BWNwA6AA4PUIBmNwDIj0NDADYAgHYAgF90nzQsPAJicipaBmZ0dm4+QVEJaVkFZVV1LV0DAEZTJBBkSxs7R07XBE8ffwRWtxTggISkxPHDFLdE0JTM7IwcfCJSCho6JlZgxnNcCCt7KA0wSlx7CDYIBzBg84A3cwBrZ+PTq5u73gOV50WC4WwOIwdCzWcH9UCDACcKUSwRSCOWKVCs0MCPGI0QHlxwQ8HkSiUirVahgCiQ8yLWXQ2eW2hT2JUOPzOFz+t3uj3szzen2+JwgPIBQJBYL6+naTm6ML6TkRyNR6JWWLJOLxfkQkVCIUSaNCyQCCLioVxGSyjNyWwKu2KBzKR1F50u115bDo6HM6GCyEYYIAZn7KK7fp6JfZgehQbDIfKerDlYgkSi0RjNYltW58UNwsEYnFCQi3G5WmaGTlNvkdkV9qUWMFYNhzAB3d3i+68AAyAHlqvReyptInOgreg5U2Nlh4izmEQjwilWuFIil8xT5ylDBbIokrVEFtWmfb62znc3Wx33WRzN22H3B+Jh6Px9Cp3CXIhWnOFziy50muHgbluUTBK0aK4piKQkpE5qZDa9jmBAcDymedask6TZJoq04DIgAC0iT5kRATBEuVHUdRJqnnaWGOo2hzYFYEBod+k4poRYwmoYxLosue5uIYUFrluAStJBhKaq0EQiZibj0bWLJMeyLqcl2UYQHhX4zqk-GZgaaTxFE6L5uWFGRIYbjhEsHhrgeCLKcyDoNup15tp23Labp3Hwr+bhlsECyhGuASEtEhJ5rqCAUpExJ7g51lwakATWusDGqe5V4tl5d4Pr5E7JkqPHjCaAHLgaYWJJ4ATgQlRpLlBKzwQioFIekQA */
  id: 'purchaseReqNumberAlert',

  context: ({ input }) => ({
    apolloClient: input.apolloClient,
    purchaseReqNumberStream: input.purchaseReqNumberStream,

    purchaseReqNumber: null,
    demandId: null,
    previousPurchaseReqNumber: null,
  }),

  initial: 'hidden',
  states: {
    hidden: {
      entry: 'resetContext',
    },
    loadingDemand: {
      entry: 'resetDemandId',
      invoke: {
        src: 'loadDemandConversation',
        id: 'loadDemandConversation',
        input: ({ context }) => ({
          apolloClient: context.apolloClient,
          purchaseReqNumber: context.purchaseReqNumber!,
        }),
        onDone: [
          {
            actions: { type: 'setDemandConversationDetails', params: ({ event }) => event.output },
            target: 'showingDemand',
            guard: { type: 'hasDemandConversationDetails', params: ({ event }) => event.output },
          },
          { target: 'showingNoDemand' },
        ],
        onError: {
          actions: 'logError',
          target: 'showingNoDemand',
        },
      },
    },
    showingDemand: {
      on: {
        NAVIGATE_TO_DEMAND: {
          actions: 'setPreviousPurchaseReqNumber',
          target: 'hidden',
        },
        CLOSE_ALERT: 'hidden',
      },
    },
    showingNoDemand: {
      on: {
        CLOSE_ALERT: 'hidden',
      },
    },
  },

  invoke: {
    src: 'subscribeToPurchaseReqNumberChanges',
    id: 'subscribeToPurchaseReqNumberChanges',
    input: ({ context }) => context.purchaseReqNumberStream,
  },

  on: {
    PURCHASE_REQ_NUMBER_UPDATED: [
      {
        target: '#purchaseReqNumberAlert.loadingDemand',
        actions: 'setPurchaseReqNumber',
        guard: 'hasPurchaseReqNumber',
      },
      { target: '#purchaseReqNumberAlert.hidden', actions: 'setPurchaseReqNumber' },
    ],
  },
});

export type ExtensionPurchaseReqNumberSnapshot = SnapshotFrom<typeof extensionPurchaseReqNumberMachine>;
